import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { Shield, ShieldCheck, Trash2, UserPlus } from 'lucide-react'
import { pb } from '../../lib/pocketbase'
import { useAuth } from '../../state/AuthContext'

type UserRole = 'admin' | 'editor'

interface ManagedUser {
  id: string
  email: string
  name: string
  role?: UserRole
  verified?: boolean
  created: string
}

interface UserFormState {
  name: string
  email: string
  password: string
  role: UserRole
}

const initialForm: UserFormState = { name: '', email: '', password: '', role: 'editor' }

export function UserManagerPage() {
  const { user, isAdmin } = useAuth()
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<UserFormState>(initialForm)

  const adminCount = useMemo(() => users.filter((item) => item.role === 'admin').length, [users])

  useEffect(() => {
    if (!isAdmin) return
    pb.collection('users')
      .getFullList<ManagedUser>({ sort: 'name,email' })
      .then(setUsers)
      .catch(() => setError('Não foi possível carregar os usuários.'))
      .finally(() => setLoading(false))
  }, [isAdmin])

  if (!isAdmin) return <Navigate to="/criador-de-midias" replace />

  const createUser = async (event: FormEvent) => {
    event.preventDefault()
    setCreating(true)
    setError('')
    try {
      const saved = await pb.collection('users').create<ManagedUser>({
        email: form.email.trim(),
        emailVisibility: true,
        password: form.password,
        passwordConfirm: form.password,
        name: form.name.trim(),
        role: form.role,
      })
      setUsers((current) => [...current, saved].sort(sortUsers))
      setForm(initialForm)
    } catch {
      setError('Não foi possível criar este usuário. Verifique e-mail e senha.')
    } finally {
      setCreating(false)
    }
  }

  const updateRole = async (target: ManagedUser, role: UserRole) => {
    if (target.id === user?.id && role !== 'admin') {
      setError('Você não pode remover o próprio acesso admin.')
      return
    }
    setBusyId(target.id)
    setError('')
    try {
      const saved = await pb.collection('users').update<ManagedUser>(target.id, { role })
      setUsers((current) => current.map((item) => item.id === saved.id ? saved : item).sort(sortUsers))
    } catch {
      setError('Não foi possível alterar o acesso deste usuário.')
    } finally {
      setBusyId('')
    }
  }

  const deleteUser = async (target: ManagedUser) => {
    if (target.id === user?.id) {
      setError('Você não pode apagar o próprio usuário.')
      return
    }
    setBusyId(target.id)
    setError('')
    try {
      await pb.collection('users').delete(target.id)
      setUsers((current) => current.filter((item) => item.id !== target.id))
    } catch {
      setError('Não foi possível apagar este usuário.')
    } finally {
      setBusyId('')
    }
  }

  return (
    <div className="users-admin">
      <div className="bio-admin-header">
        <div>
          <span className="bio-admin-kicker">LORENA MAGAZINE</span>
          <h1>Gerenciador de Usuários</h1>
          <p>Crie acessos e defina quem pode administrar usuários do sistema.</p>
        </div>
      </div>

      {error && <p className="bio-admin-error">{error}</p>}

      <section className="users-panel">
        <div className="users-panel-heading">
          <div>
            <h2>Novo acesso</h2>
            <p>Usuários criados aqui já entram verificados e podem acessar o admin imediatamente.</p>
          </div>
          <UserPlus size={18} />
        </div>
        <form className="users-create-form" onSubmit={createUser}>
          <label>
            <span>Nome</span>
            <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Nome da pessoa" required />
          </label>
          <label>
            <span>E-mail</span>
            <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} placeholder="email@lorenamagazine.com" required />
          </label>
          <label>
            <span>Senha inicial</span>
            <input type="password" minLength={8} value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} placeholder="mínimo 8 caracteres" required />
          </label>
          <label>
            <span>Acesso</span>
            <select value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value as UserRole }))}>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <button type="submit" disabled={creating}>{creating ? 'Criando…' : 'Adicionar usuário'}</button>
        </form>
      </section>

      <section className="users-panel">
        <div className="users-panel-heading">
          <div>
            <h2>Usuários cadastrados</h2>
            <p>{users.length} usuários · {adminCount} admins</p>
          </div>
        </div>

        {loading ? <p className="bio-admin-loading">Carregando…</p> : (
          <div className="users-table" role="table" aria-label="Usuários cadastrados">
            <div className="users-table-row header" role="row">
              <span>Usuário</span>
              <span>Acesso</span>
              <span>Criado em</span>
              <span>Ações</span>
            </div>
            {users.map((item) => (
              <div className="users-table-row" role="row" key={item.id}>
                <div className="users-identity">
                  <strong>{item.name || 'Sem nome'}</strong>
                  <span>{item.email}</span>
                </div>
                <span className={`users-role ${item.role === 'admin' ? 'admin' : 'editor'}`}>
                  {item.role === 'admin' ? <ShieldCheck size={14} /> : <Shield size={14} />}
                  {item.role === 'admin' ? 'Admin' : 'Editor'}
                </span>
                <span>{formatDate(item.created)}</span>
                <div className="users-actions">
                  {item.role === 'admin' ? (
                    <button type="button" disabled={busyId === item.id || item.id === user?.id} onClick={() => updateRole(item, 'editor')}>Tornar editor</button>
                  ) : (
                    <button type="button" disabled={busyId === item.id} onClick={() => updateRole(item, 'admin')}>Promover admin</button>
                  )}
                  <button type="button" className="icon-danger" disabled={busyId === item.id || item.id === user?.id} onClick={() => deleteUser(item)} aria-label={`Apagar ${item.name || item.email}`} title="Apagar usuário"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function sortUsers(a: ManagedUser, b: ManagedUser) {
  return (a.name || a.email).localeCompare(b.name || b.email)
}

function formatDate(value: string) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
}
